import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { PageHeader } from '@aigovops/design-system';
import { AiProviderSettings } from '../components/AiProviderSettings';
import { DataModeSettings } from '../components/DataModeSettings';
import { FieldClassSettings } from '../components/FieldClassSettings';
import { CustomerAiProviders } from '../components/CustomerAiProviders';
import { FederalParityStatus } from '../components/FederalParityStatus';
import { CiIdentitySettings } from '../components/CiIdentitySettings';
import { useWorkspace } from '../lib/workspace';

/**
 * Security settings page — /w/:slug/app/settings/security.
 *
 * PR-P1-2 · ADR-0012 §12.4 "Security is a new Settings subpage for HMAC
 * rotation + retention + assessor invites — spec in ADR-0011 references
 * it; UI is R8 or later."
 *
 * Real content per the ADRs:
 *   - HMAC key rotation (ADR-0011 §11.6) — key material lives in the
 *     Cloudflare-plane KMS binding; this page surfaces the workspace-
 *     scoped rotation controls.
 *   - Audit-log retention class (ADR-0014 chain-of-custody + workspace
 *     retention windows).
 *   - Assessor invites summary (ADR-0011 §11.7) — cross-link to
 *     `/settings/assessors` for the full engagement list.
 *
 * SSO enforcement toggle lives on `/settings/sso` (ADR-0015) — this
 * page never duplicates it.
 */
export function Security() {
  const { workspace, role } = useWorkspace();
  // V101-5-4: the coach's "Allow a provider" deep-links to #ai-providers.
  const { hash, search } = useLocation();
  useEffect(() => {
    // V105-2-1/2-3: the federal-parity actions deep-link to #data-mode / #data-classes too.
    const target = ['#ai-providers', '#data-mode', '#data-classes', '#your-ai-providers', '#ai-features', '#ci-identities'].includes(hash) ? hash.slice(1) : null;
    if (!target) return;
    // Round 3: a prefilled CI-identity trust owns focus (the form's repository field), so only scroll there.
    const prefilled = target === 'ci-identities' && /[?&]ci_(repo|workflow)=/.test(search);
    const t = window.setTimeout(() => { const el = document.getElementById(target); el?.scrollIntoView?.({ block: 'start' }); if (!prefilled) el?.focus(); }, 0);
    return () => window.clearTimeout(t);
  }, [hash, search]);
  return (
    <div style={{ display: 'grid', gap: 'var(--space-6)' }}>
      <PageHeader variant="app" title="Security" />
      <DataModeSettings slug={workspace.slug} role={role} />
      <FederalParityStatus slug={workspace.slug} role={role} />
      <AiProviderSettings slug={workspace.slug} role={role} />
      <CustomerAiProviders slug={workspace.slug} role={role} />
      <FieldClassSettings slug={workspace.slug} role={role} />
      {/* ADR-0074 owner decision 2026-10-09: self-service CI verification identities. */}
      <CiIdentitySettings slug={workspace.slug} role={role} />
    </div>
  );
}
